"use client";

import React, { useState } from "react";
import { ChevronRight, ChevronLeft, CircleCheck, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
export default function ApplicationForm() {
  return <section data-section-id="135611" id="puppy-application" className="w-full py-16 md:py-24 bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6 max-w-3xl">
        <div className="text-center mb-10">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-foreground mb-4 tracking-tight">
            Puppy Application Form
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-sans">
            We take great care in placing our Cobrnik Cavaliers in the best homes. Please complete this application to help us understand your family and home environment.
          </p>
        </div>

        <div className="bg-card border border-border rounded-xl shadow-lg overflow-hidden">
          <FormProvider formKey="puppy_application_contact_application" formId="puppy_application_contact_application" sectionName="Puppy Application" defaultValues={{
          applicant_name: "",
          location: "",
          preferred_gender: "",
          home_type: "",
          email: "",
          phone: "",
          breed_experience: "",
          cavalier_color_preference: "",
          veterinary_reference: ""
        }} validationRules={{
          applicant_name: {
            required: true
          },
          location: {
            required: true
          },
          preferred_gender: {
            required: true
          },
          home_type: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          breed_experience: {
            required: false
          },
          cavalier_color_preference: {
            required: false
          },
          veterinary_reference: {
            required: false
          }
        }} submitText="Submit Application" successMessage="Your application for a Cobrnik Cavalier has been successfully submitted. We review every application carefully and will be in touch soon.">
            <WizardForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function WizardForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [step, setStep] = useState(1);
  const totalSteps = 3;
  const progress = step / totalSteps * 100;
  const nextStep = () => setStep(s => Math.min(s + 1, totalSteps));
  const prevStep = () => setStep(s => Math.max(s - 1, 1));
  if (isSubmitted && !submitError) {
    return <div className="p-10 text-center flex flex-col items-center justify-center min-h-[400px]">
        <div className="w-16 h-16 bg-primary/5 rounded-full flex items-center justify-center mb-6">
          <CircleCheck className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-2xl font-serif font-semibold text-foreground mb-4">Application Received</h3>
        <p className="text-muted-foreground mb-8 max-w-md font-sans">
          {successMessage}
        </p>
        <Button onClick={() => {
        resetForm();
        setStep(1);
      }} variant="outline" size="lg" className="rounded-full border-primary text-primary hover:bg-primary/5">
          Submit Another Application
        </Button>
      </div>;
  }
  return <div className="flex flex-col h-full">
      <div className="p-6 md:p-8 border-b border-border bg-muted/20">
        <div className="flex justify-between items-end mb-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-1">Step {step} of {totalSteps}</p>
            <h3 className="text-xl font-serif font-semibold text-foreground">
              {step === 1 && "Applicant Details"}
              {step === 2 && "Home & Family"}
              {step === 3 && "Cavalier Interest"}
            </h3>
          </div>
          <span className="text-sm text-muted-foreground font-medium font-sans">{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="h-1.5 bg-secondary" />
      </div>

      <div className="p-6 md:p-8 flex-grow">
        <Form onSubmit={handleSubmit} className="space-y-0">
          
          <div className={step === 1 ? "space-y-6 block" : "hidden"}>
            <FormField control={control} name="applicant_name" rules={{
            required: "Full name is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-base font-serif">Applicant Full Name</FormLabel>
                  <FormControl>
                    <Input className="h-11 text-base rounded-lg border-border focus-visible:ring-primary/20" placeholder="Jane Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="location" rules={{
            required: "City and State are required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-base font-serif">City & State</FormLabel>
                  <FormControl>
                    <Input type="text" className="h-11 text-base rounded-lg border-border focus-visible:ring-primary/20" placeholder="Charleston, SC" {...field} />
                  </FormControl>
                  <FormDescription className="font-sans text-xs">Please provide your current place of residence.</FormDescription>
                  <FormMessage />
                </FormItem>} />
          </div>

          <div className={step === 2 ? "space-y-6 block" : "hidden"}>
            <FormField control={control} name="home_type" rules={{
            required: "Home description is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-base font-serif">Type of Home & Yard</FormLabel>
                  <FormControl>
                    <Input className="h-11 text-base rounded-lg border-border focus-visible:ring-primary/20" placeholder="e.g. House with fenced yard, Apartment" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="email" rules={{
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-base font-serif">Email Address</FormLabel>
                  <FormControl>
                    <Input type="email" className="h-11 text-base rounded-lg border-border focus-visible:ring-primary/20" placeholder="jane.doe@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="phone" rules={{
            required: "Phone number is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-base font-serif">Phone Number</FormLabel>
                  <FormControl>
                    <Input type="tel" className="h-11 text-base rounded-lg border-border focus-visible:ring-primary/20" placeholder="(555) 000-0000" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          <div className={step === 3 ? "space-y-6 block" : "hidden"}>
            <FormField control={control} name="preferred_gender" rules={{
            required: "Please select a preference"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-base font-serif">Gender Preference</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-11 w-full rounded-lg border-border focus-visible:ring-primary/20">
                        <SelectValue placeholder="Select Preference" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="No Preference">No Preference</SelectItem>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="cavalier_color_preference" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-base font-serif">Cavalier color preference</FormLabel>
                  <FormControl>
                    <Input className="h-11 text-base rounded-lg border-border focus-visible:ring-primary/20" placeholder="e.g. Blenheim, Tri-color" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="veterinary_reference" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-base font-serif">veterinary reference</FormLabel>
                  <FormControl>
                    <Input className="h-11 text-base rounded-lg border-border focus-visible:ring-primary/20" placeholder="" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="breed_experience" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-base font-serif">Experience with Cavaliers or Other Breeds</FormLabel>
                  <FormControl>
                    <Textarea className="min-h-[120px] rounded-lg border-border focus-visible:ring-primary/20 py-3 resize-y" placeholder="Please share your history with dogs, specifically Cavaliers if any..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          {submitError && <div className="mt-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm flex items-start font-sans">
              <p>{submitError} Please check all steps for missing information.</p>
            </div>}

          <div className="flex items-center justify-between mt-10 pt-6 border-t border-border">
            <Button type="button" variant="outline" onClick={prevStep} disabled={step === 1 || isSubmitting} className={`${step === 1 ? "invisible" : ""} rounded-full border-border font-sans`}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>

            {step < totalSteps ? <Button type="button" onClick={nextStep} className="min-w-[120px] rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-sans">
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button> : <Button type="submit" disabled={isSubmitting} className="min-w-[140px] rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-sans">
                {isSubmitting ? <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </> : <>
                    Submit Application
                    <CircleCheck className="w-4 h-4 ml-2" />
                  </>}
              </Button>}
          </div>
        </Form>
      </div>
    </div>;
}